import { SelectOption, SelectOptionValue } from '@components/v2/form/Select/select.types';
import {
  useUpdateCatalog,
  useUpdateCatalogNavigationPreferences,
} from '../../../../../../../../../services/modules/catalogs/catalogs.mutations';
import { useTranslation } from '../../../../../../../../translation/TranslationContext';
import { CatalogCreationBooleanOptions } from '../../../../../../../creation/catalogCreationForm.types';
import { CATALOG_CREATION_NAVIGATION_COLOR_MODES } from '../../../../../../../creation/catalogCreationPresentation.consts';
import { PRODUCT_WITHOUT_MEDIA_OPTIONS } from '../../../../../../../creation/components/CatalogCreationProductWithoutMediaField';
import { UNAVAILABLE_PRODUCT_COLOR_OPTIONS } from '../../../../../../../creation/components/CatalogCreationUnavailableProductColorField';
import { NavigationColorMode } from '../../../../../../../enums/navigationColorMode.enum';
import { CatalogDisplaySectionProps } from '../CatalogDisplaySection';
import {
  CATALOG_DETAILS_NAVIGATION_COLOR_MODE_LANG,
  CATALOG_DETAILS_PRODUCT_COLOR_CARDS_OPTIONS,
  CATALOG_DETAILS_RELATED_PRODUCTS_OPTIONS,
} from '../catalogDisplaySection.consts';

const ENABLED_OPTION_VALUE = 'true';

const useCatalogDisplaySection = ({
  id,
  isDynamic,
  isReadOnly,
  withMedia,
  navigationColorMode,
  groupColorsInSingleCard,
  showUnavailableProductsForSale,
  navigationFilterRelatedProducts,
}: CatalogDisplaySectionProps) => {
  const { translate } = useTranslation();

  const catalogMutation = useUpdateCatalog({ catalogId: id });
  const navigationPreferencesMutation = useUpdateCatalogNavigationPreferences({ catalogId: id });

  const isFieldDisabled = isReadOnly;

  const isNavigationLocked = Boolean(showUnavailableProductsForSale);
  const isNavigationPreferenceDisabled = isFieldDisabled || isNavigationLocked;
  const isUnavailableProductColorDisabled = isFieldDisabled;

  const toSelectOptions = (booleanOptions: CatalogCreationBooleanOptions): SelectOption[] =>
    booleanOptions.map((option) => ({
      value: String(option.value),
      label: translate(option.label),
    }));

  const values = {
    colorMode: navigationColorMode ?? NavigationColorMode.CATALOG_PRODUCT_COLORS,
    unavailableProductColor: String(Boolean(showUnavailableProductsForSale)),
    productColorCards: String(Boolean(groupColorsInSingleCard)),
    relatedProducts: String(Boolean(navigationFilterRelatedProducts)),
    productWithoutMedia: String(Boolean(withMedia)),
  };

  const options = {
    colorMode: CATALOG_CREATION_NAVIGATION_COLOR_MODES.map((colorMode) => ({
      value: colorMode,
      label: translate(CATALOG_DETAILS_NAVIGATION_COLOR_MODE_LANG[colorMode]),
    })),
    unavailableProductColor: toSelectOptions(UNAVAILABLE_PRODUCT_COLOR_OPTIONS),
    productColorCards: toSelectOptions(CATALOG_DETAILS_PRODUCT_COLOR_CARDS_OPTIONS),
    relatedProducts: toSelectOptions(CATALOG_DETAILS_RELATED_PRODUCTS_OPTIONS),
    productWithoutMedia: toSelectOptions(PRODUCT_WITHOUT_MEDIA_OPTIONS),
  };

  const submitColorMode = (value: SelectOptionValue) =>
    navigationPreferencesMutation.mutate({ navigationColorMode: value as NavigationColorMode });

  const submitRelatedProducts = (value: SelectOptionValue) =>
    navigationPreferencesMutation.mutate({
      navigationFilterRelatedProducts: value === ENABLED_OPTION_VALUE,
    });

  const submitUnavailableProductColor = (value: SelectOptionValue) =>
    catalogMutation.mutate({ showUnavailableProductsForSale: value === ENABLED_OPTION_VALUE });

  const submitProductColorCards = (value: SelectOptionValue) =>
    catalogMutation.mutate({ groupColorsInSingleCard: value === ENABLED_OPTION_VALUE });

  const submitProductWithoutMedia = (value: SelectOptionValue) =>
    catalogMutation.mutate({ withMedia: value === ENABLED_OPTION_VALUE });

  return {
    isDynamic,
    isFieldDisabled,
    isNavigationLocked,
    isNavigationPreferenceDisabled,
    isUnavailableProductColorDisabled,
    values,
    options,
    submitColorMode,
    submitUnavailableProductColor,
    submitProductColorCards,
    submitProductWithoutMedia,
    submitRelatedProducts,
  };
};

export default useCatalogDisplaySection;
